import { useState } from 'react'
import { Calendar } from 'lucide-react'

// Indian FY: April–March
function getCurrentFY() {
  const now = new Date()
  const month = now.getMonth() // 0-indexed
  const year = month >= 3 ? now.getFullYear() : now.getFullYear() - 1
  return year // start year of FY, e.g. 2026 for FY 2026-27
}

function fyLabel(startYear) {
  return `FY ${startYear}-${String(startYear + 1).slice(2)}`
}

function getMonthRange(date) {
  const y = date.getFullYear()
  const m = date.getMonth()
  const from = `${y}-${String(m + 1).padStart(2, '0')}-01`
  const lastDay = new Date(y, m + 1, 0).getDate()
  const to = `${y}-${String(m + 1).padStart(2, '0')}-${lastDay}`
  return { from, to }
}

function getQuarterRange(quarter, fyStart) {
  // Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar
  const quarterMonths = {
    Q1: [3, 4, 5],    // Apr, May, Jun
    Q2: [6, 7, 8],    // Jul, Aug, Sep
    Q3: [9, 10, 11],  // Oct, Nov, Dec
    Q4: [0, 1, 2],    // Jan, Feb, Mar
  }
  const months = quarterMonths[quarter]
  const startYear = quarter === 'Q4' ? fyStart + 1 : fyStart
  const startMonth = months[0]
  const endMonth = months[2]
  const from = `${startYear}-${String(startMonth + 1).padStart(2, '0')}-01`
  const endYear = quarter === 'Q4' ? fyStart + 1 : fyStart
  const lastDay = new Date(endYear, endMonth + 1, 0).getDate()
  const to = `${endYear}-${String(endMonth + 1).padStart(2, '0')}-${lastDay}`
  return { from, to }
}

function getFYRange(fyStart) {
  return {
    from: `${fyStart}-04-01`,
    to: `${fyStart + 1}-03-31`,
  }
}

const PRESETS = [
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'fy', label: 'Full FY' },
  { id: 'custom', label: 'Custom' },
]

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4']

export default function PeriodSelector({ value, onChange }) {
  const [preset, setPreset] = useState('thisMonth')
  const [quarter, setQuarter] = useState('Q1')
  const [fyStart, setFyStart] = useState(getCurrentFY())
  const [customFrom, setCustomFrom] = useState(value?.from || '')
  const [customTo, setCustomTo] = useState(value?.to || '')

  const currentFY = getCurrentFY()
  // Show current + 2 previous FYs
  const fyOptions = [currentFY, currentFY - 1, currentFY - 2]

  const handlePreset = (id) => {
    setPreset(id)
    const now = new Date()
    if (id === 'thisMonth') {
      onChange(getMonthRange(now))
    } else if (id === 'lastMonth') {
      const last = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      onChange(getMonthRange(last))
    } else if (id === 'quarter') {
      onChange(getQuarterRange(quarter, fyStart))
    } else if (id === 'fy') {
      onChange(getFYRange(fyStart))
    }
    // custom: user picks dates manually
  }

  const handleQuarterChange = (q) => {
    setQuarter(q)
    if (preset === 'quarter') onChange(getQuarterRange(q, fyStart))
  }

  const handleFYChange = (fy) => {
    const f = Number(fy)
    setFyStart(f)
    if (preset === 'fy') onChange(getFYRange(f))
    else if (preset === 'quarter') onChange(getQuarterRange(quarter, f))
  }

  const handleCustomApply = () => {
    if (customFrom && customTo) onChange({ from: customFrom, to: customTo })
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar className="h-4 w-4 text-gray-400" />

      {/* Preset buttons */}
      <div className="flex gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => handlePreset(p.id)}
            className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
              preset === p.id ? 'bg-brand-100 text-brand-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Quarter selector */}
      {preset === 'quarter' && (
        <div className="flex items-center gap-1.5">
          <div className="flex gap-0.5">
            {QUARTERS.map((q) => (
              <button
                key={q}
                onClick={() => handleQuarterChange(q)}
                className={`px-2 py-1 text-xs font-medium rounded ${
                  quarter === q ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {q}
              </button>
            ))}
          </div>
          <select className="select text-xs py-1 w-auto" value={fyStart} onChange={(e) => handleFYChange(e.target.value)}>
            {fyOptions.map((fy) => <option key={fy} value={fy}>{fyLabel(fy)}</option>)}
          </select>
        </div>
      )}

      {/* FY selector */}
      {preset === 'fy' && (
        <select className="select text-xs py-1 w-auto" value={fyStart} onChange={(e) => handleFYChange(e.target.value)}>
          {fyOptions.map((fy) => <option key={fy} value={fy}>{fyLabel(fy)}</option>)}
        </select>
      )}

      {/* Custom range */}
      {preset === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input type="date" className="input text-xs py-1 w-auto" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} onKeyDown={(e) => e.preventDefault()} />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" className="input text-xs py-1 w-auto" value={customTo} onChange={(e) => setCustomTo(e.target.value)} onKeyDown={(e) => e.preventDefault()} />
          <button onClick={handleCustomApply} className="btn-primary text-xs py-1 px-2.5" disabled={!customFrom || !customTo}>Apply</button>
        </div>
      )}

      {/* Show selected range */}
      {value?.from && value?.to && (
        <span className="text-xs text-gray-400 ml-1">
          {value.from} → {value.to}
        </span>
      )}
    </div>
  )
}
